import * as React from "react";
import Modal from "@mui/material/Modal";
import {
  StyledBox,
  StyledBoxCard,
  StyledMain,
  StyledTypographyOrder,
} from "./style";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import theme from "../../../theme";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { VIRTUAL_LOCKER_GET_ITEM } from "../../../actions/VirtualLockerAction";
import { API } from "../../../api";
import { StyledBoxFlex } from "../success-virtual-locker/style";
import { StyledTypography } from "../manual-split-calculation/style";
import CrossIcon from "../../../assets/Icons/CrossIcon";
import {
  CUSTOMER_ORDER_DETAIL,
  CUSTOMER_ORDER_LIST,
  CUSTOMER_ORDER_UPDATE,
  ORDER_CREATE,
} from "../../../actions/OrderAction";

function normalizeItemsResponse(apiBody) {
  if (!apiBody) return [];
  if (Array.isArray(apiBody)) return apiBody;
  if (Array.isArray(apiBody.data)) return apiBody.data;
  return [];
}

function getLockerItemId(source) {
  if (!source) return "";
  if (source.itemId != null) return String(source.itemId).trim();
  if (source.item?.id != null) return String(source.item.id).trim();
  if (source.value != null) return String(source.value).trim();
  if (source.id != null) return String(source.id).trim();
  return "";
}

function toPickerOption(source) {
  const value = getLockerItemId(source);
  if (!value) return null;
  const item = source?.item || source;
  return {
    label: item?.product_name || source?.label || "Product",
    value,
  };
}

const validationSchema = Yup.object({
  selectedItems: Yup.array(),
});

/**
 * @param {"create"|"append"} mode - create: POST /order; append: PATCH /order/:id with orderDetails
 */
export default function CreateOrder({
  open,
  onClose,
  mode = "create",
  orderId,
  existingOrderDetails = [],
}) {
  const dispatch = useDispatch();
  const submitLock = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isOrderLoading = useSelector((state) => state?.order?.isLoading);
  const [pickerItems, setPickerItems] = React.useState([]);
  const [pickerLoading, setPickerLoading] = React.useState(false);
  const isSubmitDisabled = isSubmitting || isOrderLoading || pickerLoading;

  const existingByItemId = React.useMemo(() => {
    const map = new Map();
    (existingOrderDetails || []).forEach((line) => {
      const id = getLockerItemId(line);
      if (id) map.set(id, line);
    });
    return map;
  }, [existingOrderDetails]);

  const initialSelectedItems = React.useMemo(() => {
    if (mode === "append") {
      return (existingOrderDetails || [])
        .map((line) => toPickerOption(line))
        .filter(Boolean);
    }
    return pickerItems.map((item) => toPickerOption(item)).filter(Boolean);
  }, [mode, existingOrderDetails, pickerItems]);

  const availableItems = React.useMemo(() => {
    const byId = new Map();
    pickerItems.forEach((item) => {
      const opt = toPickerOption(item);
      if (opt) byId.set(opt.value, opt);
    });
    (existingOrderDetails || []).forEach((line) => {
      const opt = toPickerOption(line);
      if (opt && !byId.has(opt.value)) byId.set(opt.value, opt);
    });
    return Array.from(byId.values());
  }, [pickerItems, existingOrderDetails]);

  const getItemLocker = React.useCallback(
    (values) => {
      dispatch({
        type: VIRTUAL_LOCKER_GET_ITEM,
        payload: values ?? {},
      });
    },
    [dispatch]
  );

  React.useEffect(() => {
    if (!open) {
      submitLock.current = false;
      setIsSubmitting(false);
      return undefined;
    }

    let cancelled = false;
    setPickerLoading(true);
    API.virtualGet({})
      .then((res) => {
        if (cancelled) return;
        setPickerItems(normalizeItemsResponse(res.data));
      })
      .catch(() => {
        if (!cancelled) setPickerItems([]);
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const modalTitle = mode === "append" ? "Add products" : "Create Order";
  const submitLabel = mode === "append" ? "Update order" : "Create Order";

  const submitOrder = (values, releaseSubmit) => {
    if (submitLock.current || isSubmitDisabled) return;

    submitLock.current = true;
    setIsSubmitting(true);

    const finish = () => {
      submitLock.current = false;
      setIsSubmitting(false);
      releaseSubmit?.();
    };

    if (mode === "append" && orderId) {
      const orderDetails = values.selectedItems
        .map((sel) => {
          const itemId = getLockerItemId(sel);
          if (!itemId) return null;
          const existing = existingByItemId.get(itemId);
          if (existing) {
            return {
              itemId,
              quantity: existing.quantity ?? 0,
              price: existing.price ?? 0,
              totalPrice: existing.totalPrice ?? 0,
              status: existing.status ?? "pending",
            };
          }
          return {
            itemId,
            quantity: 0,
            price: 0,
            totalPrice: 0,
            status: "pending",
          };
        })
        .filter(Boolean);

      dispatch({
        type: CUSTOMER_ORDER_UPDATE,
        payload: {
          id: orderId,
          orderDetails,
          onSuccess: () => {
            onClose();
            getItemLocker();
            dispatch({ type: CUSTOMER_ORDER_DETAIL, payload: { id: orderId } });
            dispatch({ type: CUSTOMER_ORDER_LIST, payload: { page: 1, limit: 10 } });
            finish();
          },
          onError: finish,
        },
      });
      return;
    }

    dispatch({
      type: ORDER_CREATE,
      payload: {
        ...(values.selectedItems.length
          ? { selectedItems: values.selectedItems }
          : { explicitEmptyOrder: true }),
        onSuccess: () => {
          onClose();
          getItemLocker();
          dispatch({ type: CUSTOMER_ORDER_LIST, payload: { page: 1, limit: 10 } });
          finish();
        },
        onError: finish,
      },
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <StyledBox>
        <StyledBoxCard>
          <StyledBoxFlex>
            <StyledTypography>{modalTitle}</StyledTypography>
          </StyledBoxFlex>
          <CrossIcon color={theme.palette.custom.white} onClick={onClose} />
        </StyledBoxCard>

        <Formik
          key={
            mode === "append"
              ? `append-${orderId}-${open}`
              : `create-${open}-${pickerItems.length}`
          }
          initialValues={{
            selectedItems: initialSelectedItems,
          }}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => submitOrder(values)}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Box>
                <StyledMain>
                  <Grid container spacing={2}>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <StyledTypographyOrder>Select Product</StyledTypographyOrder>
                      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                        All locker products are selected automatically. You can remove or
                        change items before creating the order. Empty order is allowed.
                      </Typography>
                      <Autocomplete
                        multiple
                        loading={pickerLoading}
                        options={availableItems}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          getLockerItemId(option) === getLockerItemId(value)
                        }
                        value={values.selectedItems}
                        onChange={(e, newValue) =>
                          setFieldValue("selectedItems", newValue)
                        }
                        disableCloseOnSelect
                        limitTags={2}
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          return (
                            <li key={key} {...optionProps}>
                              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                              {option.label}
                            </li>
                          );
                        }}
                        noOptionsText={
                          pickerLoading ? "Loading…" : "No products in your locker"
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Locker products"
                            error={
                              touched.selectedItems &&
                              Boolean(errors.selectedItems)
                            }
                            helperText={
                              touched.selectedItems && errors.selectedItems
                                ? errors.selectedItems
                                : !pickerLoading
                                  ? `${values.selectedItems.length} of ${availableItems.length} locker item(s) selected`
                                  : undefined
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {pickerLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={isSubmitDisabled}
                      >
                        {isSubmitDisabled ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          submitLabel
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </StyledMain>
              </Box>
            </Form>
          )}
        </Formik>
      </StyledBox>
    </Modal>
  );
}
